import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { useAuth, ProKind } from './auth';

export type ActiveRole = 'nutri' | 'coach';

interface RoleContextValue {
  /** Ruolo attualmente visualizzato (nutri o coach). */
  role: ActiveRole;
  /** Imposta il ruolo (no-op se il pro non è 'both'). */
  setRole: (r: ActiveRole) => void;
  /** Il pro è abilitato a entrambi i ruoli (mostra switcher). */
  canSwitch: boolean;
  /** Il pro_kind raw dal backend (può essere null per legacy). */
  proKind: ProKind | null;
}

const RoleContext = createContext<RoleContextValue | null>(null);

const STORAGE_KEY = 'dietamenu-pro-active-role';

/**
 * Determina il ruolo iniziale:
 *  - se proKind='nutri' → forza 'nutri'
 *  - se proKind='coach' → forza 'coach'
 *  - se proKind='both' → leggi da localStorage, fallback 'nutri'
 *  - se null/undefined (legacy pre-Sprint-6) → 'nutri'
 */
function resolveInitialRole(proKind: ProKind | null | undefined): ActiveRole {
  if (proKind === 'coach') return 'coach';
  if (proKind === 'both') {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'coach' || saved === 'nutri') return saved;
    } catch {
      // localStorage non disponibile (SSR, private mode) → fallback
    }
    return 'nutri';
  }
  // 'nutri' o null/undefined
  return 'nutri';
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const proKind: ProKind | null = (user?.pro_kind ?? null) as ProKind | null;
  const [role, setRoleState] = useState<ActiveRole>(() => resolveInitialRole(proKind));

  // Quando user cambia (refresh, login), riallinea con il backend.
  useEffect(() => {
    setRoleState(resolveInitialRole(proKind));
  }, [proKind]);

  const canSwitch = proKind === 'both';

  const setRole = (r: ActiveRole) => {
    if (!canSwitch) return; // lock se ruolo singolo
    setRoleState(r);
    try {
      localStorage.setItem(STORAGE_KEY, r);
    } catch {
      // ignore
    }
  };

  // Setta attributo su <body> per rule CSS conditional che il design definisce.
  // Anche se preferiamo conditional rendering React per perf, manteniamo il
  // body[data-role] hook per CSS overrides (es. tinta accent role-aware).
  useEffect(() => {
    document.body.setAttribute('data-role', role);
    return () => {
      document.body.removeAttribute('data-role');
    };
  }, [role]);

  const value = useMemo<RoleContextValue>(
    () => ({ role, setRole, canSwitch, proKind }),
    [role, canSwitch, proKind]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
